import { useEffect, useRef, useState } from 'react';
import { Shield, X } from 'lucide-react';

/**
 * Sprint 32 — Modal de step-up: pide código TOTP/backup para confirmar
 * acciones críticas post-login (cierre del día, cierre de caja).
 *
 * Aparece SOLO si el handler IPC retornó `codigo === 'STEP_UP_REQUIRED'`.
 * Tras éxito, el caller debe reintentar la acción original.
 *
 * UX:
 *  - Input auto-focus al abrir.
 *  - Acepta 6 dígitos (TOTP) o 8 chars alfanuméricos (backup code).
 *  - Escape cierra (cancela la acción).
 *  - Botones ≥ 44px (Fitts).
 *  - Mensajes naturales sin jerga.
 */
interface Props {
  abierto: boolean;
  onConfirmado: () => void;
  onCancelar: () => void;
}

export function StepUpModal({ abierto, onConfirmado, onCancelar }: Props) {
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (abierto) {
      setCodigo('');
      setError(null);
      setEnviando(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancelar();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [abierto, onCancelar]);

  if (!abierto) return null;

  const handleVerificar = async () => {
    const codigoTrim = codigo.trim();
    if (codigoTrim.length < 6) {
      setError('Escribe el código de 6 dígitos de tu app o un código de respaldo.');
      return;
    }
    setEnviando(true);
    setError(null);
    const res = await window.api.auth.verificarStepUp(codigoTrim);
    setEnviando(false);
    if (res.ok && res.data === true) {
      onConfirmado();
    } else {
      setError(res.ok ? 'Código incorrecto. Inténtalo de nuevo.' : (res.error || 'No se pudo verificar el código.'));
      setCodigo('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !enviando) {
      e.preventDefault();
      handleVerificar();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="stepup-titulo"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <h2 id="stepup-titulo" className="text-lg font-semibold text-gray-900">
              Confirma tu identidad
            </h2>
          </div>
          <button
            onClick={onCancelar}
            disabled={enviando}
            aria-label="Cancelar"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-700">
            Para esto, escribe el número de 6 cifras que aparece en{' '}
            <strong>Google Authenticator</strong> en tu celular, o una de tus claves de emergencia.
          </p>

          <div>
            <label htmlFor="stepup-codigo" className="block text-sm font-medium text-gray-700 mb-2">
              Número de tu celular
            </label>
            <input
              id="stepup-codigo"
              ref={inputRef}
              type="text"
              inputMode="text"
              autoComplete="one-time-code"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={enviando}
              placeholder="123456 o XXXXXXXX"
              className="w-full px-4 py-3 text-lg tracking-widest text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              maxLength={20}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-3 px-6 pb-6">
          <button
            onClick={onCancelar}
            disabled={enviando}
            className="flex-1 px-6 py-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 min-h-[44px]"
          >
            Cancelar
          </button>
          <button
            onClick={handleVerificar}
            disabled={enviando || codigo.trim().length < 6}
            className="flex-1 px-6 py-3 text-base font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
          >
            {enviando ? 'Verificando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}
